import { Check, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { MIN_PASSWORD_LENGTH, passwordProblems } from '@jt/domain';

/**
 * Choose a new password, and type it twice.
 *
 * Shared by the screen that forces a change on first sign-in and the one you
 * reach by choice from your account. Two copies of this drifted apart within a
 * week the last time — one enforcing twelve characters and the other eight —
 * and the rules are worth stating identically wherever they are stated.
 *
 * The rules are checked as you type using the same `passwordProblems` the API
 * runs on the way in, so the screen cannot promise something the server then
 * refuses. It is still checked on the server: this is a courtesy, not a gate.
 *
 * Problems appear only once you have typed something. A form that opens
 * already shouting reads as broken rather than helpful.
 */
export function PasswordFields({
  value,
  confirm,
  onValueChange,
  onConfirmChange,
  email,
  name,
  currentPassword,
  label = 'Your new password',
}: {
  value: string;
  confirm: string;
  onValueChange: (value: string) => void;
  onConfirmChange: (value: string) => void;
  /** Checked against the password, since it is the cheapest guess anyone has. */
  email?: string | null;
  name?: string | null;
  /** The one being replaced, so reusing it is caught here rather than by the API. */
  currentPassword?: string;
  label?: string;
}) {
  const [visible, setVisible] = useState(false);

  const problems = value.length > 0 ? passwordProblems(value, { email, name, currentPassword }) : [];
  const mismatch = confirm.length > 0 && value !== confirm;

  return (
    <>
      <label className="block">
        <span className="mb-1 block text-sm font-medium">{label}</span>
        <div className="relative">
          <input
            type={visible ? 'text' : 'password'}
            autoComplete="new-password"
            className={`input w-full pr-11 ${problems.length > 0 ? 'border-danger-400' : ''}`}
            value={value}
            onChange={(event) => onValueChange(event.target.value)}
            required
          />
          {/*
            A reveal toggle, because the alternative to seeing what you typed is
            picking something short enough to type blind twice.
          */}
          <button
            type="button"
            onClick={() => setVisible((current) => !current)}
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded-lg p-2 text-muted-foreground hover:bg-muted"
            aria-label={visible ? 'Hide password' : 'Show password'}
          >
            {visible ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>

        {problems.length > 0 ? (
          <ul className="mt-1.5 space-y-1">
            {problems.map((problem) => (
              <li key={problem} className="text-xs text-danger-600">
                {problem}
              </li>
            ))}
          </ul>
        ) : (
          <span className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            {value.length > 0 && (
              <Check className="h-3 w-3 text-secondary-600" aria-hidden="true" />
            )}
            At least {MIN_PASSWORD_LENGTH} characters. A short phrase you will remember beats a
            short password you will write down.
          </span>
        )}
      </label>

      <label className="block">
        <span className="mb-1 block text-sm font-medium">Type it again</span>
        <input
          type={visible ? 'text' : 'password'}
          autoComplete="new-password"
          className={`input w-full ${mismatch ? 'border-danger-400' : ''}`}
          value={confirm}
          onChange={(event) => onConfirmChange(event.target.value)}
          required
        />
        {mismatch && (
          <span className="mt-1 block text-xs text-danger-600">These two do not match.</span>
        )}
      </label>
    </>
  );
}

/** Whether the pair is ready to submit. Exported so callers agree on "ready". */
export const passwordPairReady = (
  value: string,
  confirm: string,
  context: { email?: string | null; name?: string | null; currentPassword?: string } = {},
): boolean => value.length > 0 && value === confirm && passwordProblems(value, context).length === 0;
